/**
 * Account-level Client Invoices API — billing issued to a Client (see
 * ClientService.ts), independent of any single project workspace. Mirrors
 * ClientService.ts's direct-fetch pattern via the shared `accountRequest`/
 * `downloadAccountFile` helpers in apiClient.ts rather than
 * PlanningApiClient's per-project `planningPath()` — this is account-scoped,
 * not project-scoped.
 */
import { accountRequest, downloadAccountFile } from './apiClient';

export type ClientInvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'overdue' | 'void';
export type ClientInvoiceArchiveFilter = 'active' | 'archived' | 'all';

export interface ClientInvoiceItem {
  id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface ClientInvoicePayment {
  id: string;
  amount: number;
  paid_at: string;
  method: string | null;
  reference: string | null;
  notes: string | null;
}

export interface ClientInvoice {
  id: string;
  created_by_email: string;
  client_id: string;
  client_name?: string;
  /** The project this invoice belongs to. Read this first. */
  project_id?: string | null;
  /** Older servers and older rows only carry this spelling; use it only when project_id is missing. */
  wedding_id?: string | null;
  source_quote_id: string | null;
  invoice_number: string;
  status: ClientInvoiceStatus;
  currency: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  /** Computed server-side as total - amount_paid. */
  balance_due: number;
  due_date: string | null;
  notes: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  items?: ClientInvoiceItem[];
  payments?: ClientInvoicePayment[];
}

export interface ClientInvoiceItemInput {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface ClientInvoiceInput {
  client_id: string;
  project_id?: string;
  items: ClientInvoiceItemInput[];
  tax_rate?: number;
  due_date?: string;
  notes?: string;
}

export interface RecordPaymentInput {
  amount: number;
  method?: string;
  reference?: string;
  notes?: string;
  paid_at?: string;
}

export interface ClientInvoiceFilters {
  client_id?: string;
  /** Only this project's invoices. */
  project_id?: string;
  status?: ClientInvoiceStatus;
  archived: ClientInvoiceArchiveFilter;
}

/** List invoices for the account, optionally filtered by client and status. */
export async function loadClientInvoices(filters: ClientInvoiceFilters): Promise<ClientInvoice[]> {
  try {
    const params = new URLSearchParams();
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.project_id) params.set('project_id', filters.project_id);
    if (filters.status) params.set('status', filters.status);
    params.set('archived', filters.archived);
    const res = await accountRequest<{ success: true; data: ClientInvoice[] }>(
      `/planning/account/client-invoices?${params.toString()}`
    );
    return res.data;
  } catch (error) {
    console.error('Failed to load client invoices:', error);
    throw error instanceof Error ? error : new Error('Failed to load client invoices.');
  }
}

export async function getClientInvoiceById(id: string): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}`);
  return res.data;
}

export async function createClientInvoice(input: ClientInvoiceInput): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>('/planning/account/client-invoices', {
    method: 'POST',
    body: input,
  });
  return res.data;
}

/** Only valid while the invoice is still a draft — the server 409s otherwise. */
export async function updateClientInvoice(id: string, input: ClientInvoiceInput): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}`, {
    method: 'PUT',
    body: input,
  });
  return res.data;
}

export async function sendClientInvoice(id: string): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}/send`, {
    method: 'POST',
  });
  return res.data;
}

export async function recordClientInvoicePayment(id: string, input: RecordPaymentInput): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}/payments`, {
    method: 'POST',
    body: input,
  });
  return res.data;
}

export async function deleteClientInvoicePayment(id: string, paymentId: string): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(
    `/planning/account/client-invoices/${id}/payments/${paymentId}`,
    { method: 'DELETE' }
  );
  return res.data;
}

export async function voidClientInvoice(id: string): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}/void`, {
    method: 'POST',
  });
  return res.data;
}

/** Un-archives a previously archived invoice. */
export async function restoreClientInvoice(id: string): Promise<ClientInvoice> {
  const res = await accountRequest<{ success: true; data: ClientInvoice }>(`/planning/account/client-invoices/${id}/restore`, {
    method: 'POST',
  });
  return res.data;
}

/** Archives the invoice (default) or permanently deletes it (only valid once already archived). */
export async function deleteClientInvoice(id: string, permanent = false): Promise<void> {
  await accountRequest<{ success: true }>(`/planning/account/client-invoices/${id}${permanent ? '?permanent=true' : ''}`, {
    method: 'DELETE',
  });
}

export async function downloadClientInvoicePdf(id: string, invoiceNumber: string): Promise<void> {
  await downloadAccountFile(`/planning/account/client-invoices/${id}/pdf`, `invoice-${invoiceNumber}.pdf`);
}
